import type { ReactNode } from 'react'
import { createContext, useContext, useEffect } from 'react'
import type { SinkTable } from './SinkTable.ts'

type Publish = (table: SinkTable | undefined) => void

/**
 * Sinks hand their numbers up to the container chrome that offers "Numbers" and "Export CSV".
 *
 * Undefined outside a container, so a sink rendered on its own (print view, tests) publishes into
 * nothing rather than failing.
 */
const SinkTableContext = createContext<Publish | undefined>(undefined)

export const SinkTablePublisher = ({ publish, children }: { publish: Publish; children: ReactNode }) => (
  <SinkTableContext.Provider value={publish}>{children}</SinkTableContext.Provider>
)

/**
 * Publishes the numbers this sink just rendered.
 *
 * Pass a memoised table: it is the effect's dependency, so a fresh object every render would
 * re-publish on every render. Pass `undefined` while the data is not ready.
 */
export function usePublishSinkTable(table: SinkTable | undefined): void {
  const publish = useContext(SinkTableContext)

  useEffect(() => {
    if (!publish) return
    publish(table)
    return () => publish(undefined)
  }, [publish, table])
}
